import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Separator } from "@/components/ui/separator";
import { router, useForm } from "@inertiajs/react";
import { AlertCircle } from "lucide-react";
import React from "react";

interface User {
  id: number;
  first_name: string;
  last_name: string;
  email_address: string;
  role: 'mentor' | 'mentee';
  category: 'pre-health' | 'graduate' | 'fellowship';
}

interface EditUserDialogProps {
  user: User;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

function EditUserDialog({ user, open, onOpenChange }: EditUserDialogProps) {
  const { data, setData, processing, errors } = useForm({
    first_name: user.first_name,
    last_name: user.last_name,
    role: user.role,
    category: user.category
  })

  const roleChanged = data.role !== user.role;
  const categoryChanged = data.category !== user.category;
  const willArchiveApplication = roleChanged || categoryChanged

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    router.patch(`/admin/users/${user.id}`, data, {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false);
      }
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl bg-white">
        <DialogHeader>
          <DialogTitle>Edit User</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-6 mt-3">
          {willArchiveApplication && (
            <Alert variant={'destructive'}>
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Changing role or category will archive their active application. They will need to complete a new application.
              </AlertDescription>
            </Alert>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-2">
              <Label htmlFor="first_name">First Name</Label>
              <Input
                id="first_name"
                value={data.first_name}
                onChange={(e) => setData('first_name', e.target.value)}
                required
              />
              {errors.first_name && (
                <p className="text-sm text-red-500">{errors.first_name}</p>
              )}
            </div>
            <div className="flex flex-col gap-2">
              <Label htmlFor="last_name">Last Name</Label>
              <Input
                id="last_name"
                value={data.last_name}
                onChange={(e) => setData('last_name', e.target.value)}
                required
              />
              {errors.last_name && (
                <p className="text-sm text-red-500">{errors.last_name}</p>
              )}
            </div>

          </div>
          <div className="flex flex-col gap-2">
            <Label>Email Address</Label>
            <Input value={user.email_address} disabled className="bg-gray-50" />
            <p className="text-xs text-gray-500">Email cannot be changed</p>
          </div>

          <div className="flex flex-col gap-3">
            <Label>Role</Label>
            <RadioGroup
              value={data.role}
              onValueChange={(value) => setData('role', value as 'mentor' | 'mentee')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mentor" id="role-mentor" />
                <Label htmlFor="role-mentor" className="font-normal cursor-pointer">
                  Mentor
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="mentee" id="role-mentee" />
                <Label htmlFor="role-mentee" className="font-normal cursor-pointer">
                  Mentee
                </Label>
              </div>
            </RadioGroup>
            {errors.role && (
              <p className="text-sm text-red-500">{errors.role}</p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            <Label>Category</Label>
            <RadioGroup
              value={data.category}
              onValueChange={(value) => setData('category', value as 'pre-health' | 'graduate' | 'fellowship')}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pre-health" id="category-prehealth" />
                <Label htmlFor="category-prehealth" className="font-normal cursor-pointer">
                  Pre-Health
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="graduate" id="category-graduate" />
                <Label htmlFor="category-graduate" className="font-normal cursor-pointer">
                  Graduate
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="fellowship" id="category-fellowship" />
                <Label htmlFor="category-fellowship" className="font-normal cursor-pointer">
                  Fellowship
                </Label>
              </div>
            </RadioGroup>
            {errors.category && (
              <p className="text-sm text-red-500">{errors.category}</p>
            )}
          </div>
          <DialogFooter>
            <Button type="button" variant={'outline'} onClick={() => onOpenChange(false)} disabled={processing}>
              Cancel
            </Button>
            <Button type="submit" disabled={processing}>{processing ? 'Saving...' : 'Save Changes'}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export default EditUserDialog;
